import React, { useState, useEffect } from 'react';

const WordSettingsModal = ({ 
  isOpen, 
  onClose, 
  settings, 
  onSettingsChange, 
  onConfirm, 
  title = "단어 설정",
  isGenerating = false,
  generationProgress = null
}) => {
  const [localSettings, setLocalSettings] = useState(settings);

  // settings prop이 변경될 때마다 localSettings 업데이트
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">{title}</h3>
        
        <div className="space-y-6">
          {/* 학교급 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">학교급</label>
            <select 
              value={localSettings.schoolLevel}
              onChange={(e) => {
                const newSettings = {...localSettings, schoolLevel: e.target.value, grade: ''};
                setLocalSettings(newSettings);
                onSettingsChange(newSettings);
              }}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">선택하세요</option>
              <option value="elementary">초등학교</option>
              <option value="middle">중학교</option>
              <option value="high">고등학교</option>
              <option value="advanced">심화과정</option>
            </select>
          </div>

          {/* 학년 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">학년</label>
            <select 
              value={localSettings.grade}
              onChange={(e) => {
                const newSettings = {...localSettings, grade: e.target.value};
                setLocalSettings(newSettings);
                onSettingsChange(newSettings);
              }}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={!localSettings.schoolLevel}
            >
              <option value="">선택하세요</option>
              {localSettings.schoolLevel === 'elementary' && (
                <>
                  <option value="3">3학년</option>
                  <option value="4">4학년</option>
                  <option value="5">5학년</option>
                  <option value="6">6학년</option>
                </>
              )}
              {localSettings.schoolLevel === 'middle' && (
                <>
                  <option value="1">1학년</option>
                  <option value="2">2학년</option>
                  <option value="3">3학년</option>
                </>
              )}
              {localSettings.schoolLevel === 'high' && (
                <>
                  <option value="1">1학년</option>
                  <option value="2">2학년</option>
                  <option value="3">3학년</option>
                </>
              )}
              {localSettings.schoolLevel === 'advanced' && (
                <>
                  <option value="1">1단계</option>
                  <option value="2">2단계</option>
                  <option value="3">3단계</option>
                </>
              )}
            </select>
          </div>

          {/* 단어 개수 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">단어 개수</label>
            <select 
              value={localSettings.wordCount}
              onChange={(e) => {
                const newSettings = {...localSettings, wordCount: e.target.value};
                setLocalSettings(newSettings);
                onSettingsChange(newSettings);
              }}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">선택하세요</option>
              <option value="5">5개</option>
              <option value="10">10개</option>
              <option value="15">15개</option>
              <option value="20">20개</option>
            </select>
          </div>

          {/* 구글 시트 URL 설정 (모든 템플릿에서 사용) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              구글 시트 URL
              <span className="text-xs text-gray-500 ml-1">(선택사항 - 학습 로그 저장용)</span>
            </label>
            <input
              type="url"
              value={localSettings.sheetUrl || ''}
              onChange={(e) => {
                const newSettings = {...localSettings, sheetUrl: e.target.value};
                setLocalSettings(newSettings);
                onSettingsChange(newSettings);
              }}
              placeholder="https://docs.google.com/spreadsheets/d/..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              구글 시트 URL을 입력하면 학습 결과가 자동으로 저장됩니다
            </p>
          </div>
        </div>

        {/* 진행률 표시 */}
        {isGenerating && generationProgress && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-center mb-3">
              <h4 className="text-lg font-semibold text-blue-800">생성 중...</h4>
              {generationProgress.currentStep && (
                <p className="text-sm text-blue-600 mt-1">{generationProgress.currentStep}</p>
              )}
            </div>
            
            <div className="space-y-2">
              {generationProgress.steps.map((step, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    step.status === 'completed' ? 'bg-green-500 text-white' :
                    step.status === 'active' ? 'bg-blue-500 text-white' :
                    'bg-gray-300 text-gray-600'
                  }`}>
                    {step.status === 'completed' ? '✓' : index + 1}
                  </div>
                  <span className={`text-sm ${
                    step.status === 'completed' ? 'text-green-700' :
                    step.status === 'active' ? 'text-blue-700' :
                    'text-gray-500'
                  }`}>
                    {step.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-3 mt-8">
          <button
            onClick={onClose}
            disabled={isGenerating}
            className={`flex-1 px-4 py-3 border border-gray-300 rounded-lg transition-colors ${
              isGenerating 
                ? 'text-gray-400 bg-gray-100 cursor-not-allowed' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            취소
          </button>
          <button
            onClick={onConfirm}
            disabled={isGenerating}
            className={`flex-1 px-4 py-3 rounded-lg transition-colors ${
              isGenerating
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            {isGenerating ? '생성 중...' : '확인'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WordSettingsModal;